import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import * as THREE from 'three';
import { useAppSelector } from '../../hooks/redux';
import { useSimulationDxfRaster } from '../../hooks/useSimulationDxfRaster';
import {
  floorRasterFocusBounds,
  floorRasterTilePlane,
  type FloorRasterDecodedTile,
  type FloorRasterViewQuery,
} from '../../utils/floorRasterTypes';
import {
  getOrthoPixelsPerWorldUnit,
  getPerspectivePixelsPerWorldUnit,
} from '../../utils/flatPlanCamera';
import { cameraDragStore } from '../../store/cameraDragStore';
const PLACEHOLDER = new THREE.Color('#ffffff');

/** Single slab while raster tiles upload — never mounts vector DXF line batches. */
const DxfRasterPlaceholder: React.FC<{ bounds: { minX: number; maxX: number; minY: number; maxY: number } }> =
  React.memo(({ bounds }) => {
    const w = Math.max(bounds.maxX - bounds.minX, 1);
    const d = Math.max(bounds.maxY - bounds.minY, 1);
    const x = (bounds.minX + bounds.maxX) / 2;
    const z = (bounds.minY + bounds.maxY) / 2;
    return (
      <mesh position={[x, 0.01, z]} rotation={[-Math.PI / 2, 0, 0]} frustumCulled>
        <planeGeometry args={[w, d]} />
        <meshBasicMaterial color={PLACEHOLDER} toneMapped={false} depthWrite={false} />
      </mesh>
    );
  });

DxfRasterPlaceholder.displayName = 'DxfRasterPlaceholder';

const GROUND = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0);
const NDC_CORNERS = [
  new THREE.Vector2(-1, -1),
  new THREE.Vector2(1, -1),
  new THREE.Vector2(1, 1),
  new THREE.Vector2(-1, 1),
];

/** Request enough density that detail tiles replace upscaled overview pyramids. */
const MIN_3D_PIXELS_PER_WORLD = 1.0;
const QUERY_QUALITY_SCALE = 1.0;
const DETAIL_TILE_BUDGET = 32;


/**
 * 3D floor is raster-only once overview tiles are on the GPU.
 * Vector lines are a brief fallback while tiles load or if raster fails.
 */
export function selectRasterDisplayTiles(
  overviewTiles: FloorRasterDecodedTile[],
  detailTiles: FloorRasterDecodedTile[],
  uploadedKeys?: ReadonlySet<string>,
  _requestedLevel?: number
): {
  overviewTiles: FloorRasterDecodedTile[];
  detailTiles: FloorRasterDecodedTile[];
  tiles: FloorRasterDecodedTile[];
  useVectorFallback: boolean;
} {
  const hasOverview = overviewTiles.length > 0;
  const filteredDetail = uploadedKeys
    ? detailTiles.filter((tile) => uploadedKeys.has(tile.key))
    : detailTiles;
  return {
    overviewTiles,
    detailTiles: filteredDetail,
    tiles: [...overviewTiles, ...filteredDetail],
    useVectorFallback: !hasOverview,
  };
}

/**
 * Keep the last complete tile set on screen until every replacement tile has
 * been uploaded. Camera pans and zooms swap the address set immediately; hiding
 * the previous image for that gap is what makes the DXF vanish mid-operation.
 */
export function retainDisplayedRasterTiles(
  previous: FloorRasterDecodedTile[],
  next: FloorRasterDecodedTile[],
  uploadedKeys: ReadonlySet<string>
): FloorRasterDecodedTile[] {
  // An empty replacement is a transient clear (bad camera sample, level flicker).
  // Dropping the previous set here is what blanks the DXF mid-orbit.
  if (next.length === 0) return previous;
  if (next.every((tile) => uploadedKeys.has(tile.key))) return next;
  const merged = new Map<string, FloorRasterDecodedTile>();
  for (const tile of previous) merged.set(tile.key, tile);
  for (const tile of next) merged.set(tile.key, tile);
  return [...merged.values()];
}

/** Positive finite distances only. Horizon and behind-camera hits are rejected. */
export function isUsableGroundDistance(
  distance: number | null,
  maxDistance: number
): distance is number {
  return (
    distance !== null &&
    Number.isFinite(distance) &&
    distance > 0 &&
    Number.isFinite(maxDistance) &&
    distance <= maxDistance
  );
}

/** Ignore NaN samples so one bad corner cannot collapse the floor to an empty tile set. */
export function finitePixelDensity(samples: number[], minimum: number): number | null {
  const finite = samples.filter((value) => Number.isFinite(value) && value > 0);
  if (finite.length === 0) return null;
  return Math.max(minimum, ...finite);
}

const RasterTileMesh: React.FC<{
  tile: FloorRasterDecodedTile;
  renderOrder: number;
  uploadIndex?: number;
  onUploaded?: (key: string) => void;
}> = React.memo(({ tile, renderOrder, uploadIndex = 0, onUploaded }) => {
  const { gl, invalidate } = useThree();
  const uploadIndexRef = useRef(uploadIndex);
  const glRef = useRef(gl);
  const invalidateRef = useRef(invalidate);
  const onUploadedRef = useRef(onUploaded);
  uploadIndexRef.current = uploadIndex;
  glRef.current = gl;
  invalidateRef.current = invalidate;
  onUploadedRef.current = onUploaded;
  const isDrawable =
    Boolean(tile.image) &&
    (typeof tile.image !== 'object' ||
      !('width' in tile.image) ||
      ((tile.image as ImageBitmap).width > 0 && (tile.image as ImageBitmap).height > 0));

  const texture = useMemo(() => {
    if (!isDrawable) return null;
    const next = new THREE.Texture(tile.image);
    next.colorSpace = THREE.SRGBColorSpace;
    next.flipY = false;
    next.wrapS = THREE.ClampToEdgeWrapping;
    next.wrapT = THREE.ClampToEdgeWrapping;
    next.repeat.y = -1;
    next.offset.y = 1;
    // Technical CAD linework requires sharp 1:1 sampling without destructive
    // mipmap box-averaging that blurs 1px lines into transparent background pixels.
    next.minFilter = THREE.LinearFilter;
    next.magFilter = THREE.LinearFilter;
    next.generateMipmaps = false;
    next.anisotropy = Math.max(8, gl.capabilities?.getMaxAnisotropy?.() ?? 16);
    next.needsUpdate = true;
    return next;
  }, [gl, isDrawable, tile.image, tile.pixelHeight, tile.pixelWidth]);
  // A closed ImageBitmap makes the next render produce a null texture. Keep the
  // GPU copy that already uploaded so the DXF does not vanish mid-operation.
  const displayedTexture = useRef<THREE.Texture | null>(null);
  if (texture) displayedTexture.current = texture;
  useEffect(() => {
    if (!texture) return;
    let active = true;
    const delayMs = Math.min(uploadIndexRef.current, 8) * 16;
    const id = window.setTimeout(() => {
      requestAnimationFrame(() => {
        if (!active) return;
        try {
          glRef.current.initTexture(texture);
        } catch {
          // The mesh is already showing; a failed preload must not hide it.
        }
        onUploadedRef.current?.(tile.key);
        invalidateRef.current();
      });
    }, delayMs);
    return () => {
      active = false;
      window.clearTimeout(id);
    };
  }, [texture, tile.key]);
  useEffect(
    () => () => {
      displayedTexture.current?.dispose();
      displayedTexture.current = null;
    },
    []
  );
  const plane = floorRasterTilePlane(tile);
  const map = displayedTexture.current;
  if (!map) return null;
  return (
    <mesh
      position={[
        plane.position[0],
        plane.position[1] + renderOrder * 0.001,
        plane.position[2],
      ]}
      rotation={[-Math.PI / 2, 0, 0]}
      renderOrder={renderOrder}
      frustumCulled={false}
    >
      <planeGeometry args={plane.size} />
      <meshBasicMaterial
        map={map}
        transparent
        depthWrite={false}
        toneMapped={false}
        side={THREE.DoubleSide}
        polygonOffset
        polygonOffsetFactor={-4}
        polygonOffsetUnits={-8}
      />
    </mesh>
  );
});

RasterTileMesh.displayName = 'RasterTileMesh';

function queryKey(query: FloorRasterViewQuery): string {
  const b = query.worldBounds;
  return [b.minX, b.minY, b.maxX, b.maxY, query.pixelsPerWorldUnit]
    .map((value) => Math.round(value * 100) / 100)
    .join('|');
}

const SimulationFloorPlanTexture3D: React.FC<{
  darkMode: boolean;
  boundsSize?: number;
  onComputingChange?: (computing: boolean) => void;
}> = ({
  darkMode: _darkMode,
  boundsSize: _boundsSize,
  onComputingChange,
}) => {
  const bounds = useAppSelector((state) => state.floorplan.bounds);
  const simulationRunning = useAppSelector((state) => state.simulation.running);
  const { camera, size, controls } = useThree();
  const initialQuery = useMemo<FloorRasterViewQuery | null>(
    () =>
      bounds
        ? {
            worldBounds: bounds,
            pixelsPerWorldUnit: Math.max(
              MIN_3D_PIXELS_PER_WORLD,
              Math.min(size.width, size.height) /
                Math.max(bounds.maxX - bounds.minX, bounds.maxY - bounds.minY, 1)
            ),
          }
        : null,
    [bounds, size.height, size.width]
  );
  const [query, setQuery] = useState<FloorRasterViewQuery | null>(initialQuery);
  const [uploadedKeys, setUploadedKeys] = useState<Set<string>>(() => new Set());
  const lastOverviewRef = useRef<FloorRasterDecodedTile[]>([]);
  const lastDetailRef = useRef<FloorRasterDecodedTile[]>([]);
  const lastQueryKey = useRef(initialQuery ? queryKey(initialQuery) : '');
  const lastCheck = useRef(0);
  const raycaster = useMemo(() => new THREE.Raycaster(), []);
  const point = useMemo(() => new THREE.Vector3(), []);

  useEffect(() => {
    if (!initialQuery) return;
    const key = queryKey(initialQuery);
    if (key === lastQueryKey.current) return;
    lastQueryKey.current = key;
    queueMicrotask(() => setQuery(initialQuery));
  }, [initialQuery]);

  useFrame((state) => {
    // Floor plan is static during playback — skip tile queries to free the main thread.
    if (simulationRunning) return;
    const now = state.clock.elapsedTime;
    // Keep the tiles already on screen while the pointer is down.
    if (cameraDragStore.isDragging()) return;
    if (!bounds || now - lastCheck.current < 0.2) return;
    lastCheck.current = now;

    const span = Math.max(bounds.maxX - bounds.minX, bounds.maxY - bounds.minY, 1);
    const maxHit = span * 8;
    let minX = Infinity;
    let minZ = Infinity;
    let maxX = -Infinity;
    let maxZ = -Infinity;
    let hits = 0;
    for (const corner of NDC_CORNERS) {
      raycaster.setFromCamera(corner, camera);
      const distance = raycaster.ray.distanceToPlane(GROUND);
      if (!isUsableGroundDistance(distance, maxHit)) continue;
      raycaster.ray.at(distance, point);
      if (!Number.isFinite(point.x) || !Number.isFinite(point.z)) continue;
      minX = Math.min(minX, point.x);
      minZ = Math.min(minZ, point.z);
      maxX = Math.max(maxX, point.x);
      maxZ = Math.max(maxZ, point.z);
      hits += 1;
    }
    const target =
      controls && 'target' in controls && controls.target instanceof THREE.Vector3
        ? controls.target
        : new THREE.Vector3(
            (bounds.minX + bounds.maxX) / 2,
            0,
            (bounds.minY + bounds.maxY) / 2
          );
    let pixelsPerWorldUnit = initialQuery?.pixelsPerWorldUnit ?? MIN_3D_PIXELS_PER_WORLD;
    if (camera instanceof THREE.PerspectiveCamera) {
      const samples =
        hits >= 2
          ? [
              [target.x, target.z],
              [minX, minZ],
              [minX, maxZ],
              [maxX, minZ],
              [maxX, maxZ],
            ]
          : [[target.x, target.z]];
      const density = finitePixelDensity(
        samples.map(([x, z]) => getPerspectivePixelsPerWorldUnit(camera, size.height, x, z)),
        MIN_3D_PIXELS_PER_WORLD
      );
      if (density === null) return;
      pixelsPerWorldUnit = density;
    } else if (camera instanceof THREE.OrthographicCamera) {
      const density = finitePixelDensity(
        [getOrthoPixelsPerWorldUnit(camera, size.height)],
        MIN_3D_PIXELS_PER_WORLD
      );
      if (density === null) return;
      pixelsPerWorldUnit = density;
    }
    if (!Number.isFinite(pixelsPerWorldUnit)) return;
    const frustum =
      hits >= 2
        ? { minX, minY: minZ, maxX, maxY: maxZ }
        : bounds;
    const viewWorld = size.width / Math.max(pixelsPerWorldUnit, 0.02);
    const next: FloorRasterViewQuery = {
      worldBounds: floorRasterFocusBounds(
        frustum,
        target.x,
        target.z,
        Math.max(viewWorld * 1.5, 200)
      ),
      pixelsPerWorldUnit:
        pixelsPerWorldUnit *
        Math.min(typeof window === 'undefined' ? 1 : window.devicePixelRatio || 1, 2) *
        QUERY_QUALITY_SCALE,
    };
    if (
      !Number.isFinite(next.worldBounds.minX) ||
      !Number.isFinite(next.worldBounds.minY) ||
      !Number.isFinite(next.worldBounds.maxX) ||
      !Number.isFinite(next.worldBounds.maxY)
    ) {
      return;
    }
    const key = queryKey(next);
    if (key === lastQueryKey.current) return;
    lastQueryKey.current = key;
    setQuery(next);
  });

  const raster = useSimulationDxfRaster(
    query,
    false,
    DETAIL_TILE_BUDGET
  );

  const detailKeysSignature = raster.detailTiles.map((tile) => tile.key).join(',');
  const overviewKeysSignature = raster.overviewTiles.map((tile) => tile.key).join(',');
  useEffect(() => {
    setUploadedKeys((prev) => {
      const keep = new Set(raster.overviewTiles.map((tile) => tile.key));
      for (const tile of raster.detailTiles) keep.add(tile.key);
      const next = new Set<string>();
      for (const key of prev) {
        if (keep.has(key)) next.add(key);
      }
      return next;
    });
  }, [detailKeysSignature, overviewKeysSignature, raster.detailTiles, raster.overviewTiles]);

  const markUploaded = useCallback((key: string) => {
    setUploadedKeys((prev) => {
      if (prev.has(key)) return prev;
      const next = new Set(prev);
      next.add(key);
      return next;
    });
  }, []);

  const { overviewTiles: displayOverview, useVectorFallback } = useMemo(
    () =>
      selectRasterDisplayTiles(
        raster.overviewTiles,
        raster.detailTiles,
        uploadedKeys,
        raster.requestedLevel
      ),
    [raster.detailTiles, raster.overviewTiles, raster.requestedLevel, uploadedKeys]
  );

  const overviewToRender = retainDisplayedRasterTiles(
    lastOverviewRef.current,
    raster.overviewTiles,
    uploadedKeys
  );
  const detailToRender = retainDisplayedRasterTiles(
    lastDetailRef.current,
    raster.detailTiles,
    uploadedKeys
  );

  const detailGpuReady =
    raster.detailTiles.length > 0 &&
    raster.detailTiles.every((tile) => uploadedKeys.has(tile.key));
  const showOverview = !detailGpuReady || detailToRender.length === 0;

  const overviewGpuReady =
    displayOverview.length > 0 &&
    displayOverview.every((tile) => uploadedKeys.has(tile.key));

  const showPlaceholder =
    Boolean(bounds) &&
    (useVectorFallback || !raster.ready || (!overviewGpuReady && !detailGpuReady));

  useEffect(() => {
    onComputingChange?.(raster.loading || (!raster.error && !overviewGpuReady && !detailGpuReady));
  }, [detailGpuReady, onComputingChange, overviewGpuReady, raster.error, raster.loading]);

  useEffect(() => {
    if (
      raster.overviewTiles.length > 0 &&
      raster.overviewTiles.every((tile) => uploadedKeys.has(tile.key))
    ) {
      lastOverviewRef.current = raster.overviewTiles;
    }
    if (
      raster.detailTiles.length > 0 &&
      raster.detailTiles.every((tile) => uploadedKeys.has(tile.key))
    ) {
      lastDetailRef.current = raster.detailTiles;
    }
  }, [raster.detailTiles, raster.overviewTiles, uploadedKeys]);

  return (
    <group name="SimulationDxfRaster3D">
      {showPlaceholder && bounds ? <DxfRasterPlaceholder bounds={bounds} /> : null}
      {showOverview &&
        overviewToRender.map((tile, index) => (
          <RasterTileMesh
            key={`overview-${tile.key}`}
            tile={tile}
            renderOrder={10}
            uploadIndex={index}
            onUploaded={markUploaded}
          />
        ))}
      {detailToRender.map((tile, index) => (
        <RasterTileMesh
          key={`detail-${tile.key}`}
          tile={tile}
          renderOrder={11}
          uploadIndex={index}
          onUploaded={markUploaded}
        />
      ))}
    </group>
  );
};

export default React.memo(SimulationFloorPlanTexture3D);
